import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  User,
  Mail,
  Globe,
  MapPin,
  Linkedin,
  UserCheck,
  ArrowLeft,
  Info,
  Shield,
  CreditCard,
  FileText,
  MessageCircle,
  CheckCircle2,
  AlertCircle,
  Building2,
} from "lucide-react";
import SearchableSelect from "../common/SearchableSelect";
import { getAllRegions, getCitiesForRegion } from "../../data/ghanaLocations";
// Removed PhoneInput import - using custom phone input instead

const Step1Enhanced = ({
  formData,
  setFormData,
  onNext,
  onPrev,
  accountType,
}) => {
  const [errors, setErrors] = useState({});
  const [customCity, setCustomCity] = useState("");
  const [availableCities, setAvailableCities] = useState([]);

  // Ensure Ghana values are always set
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      phoneCountry: "233",
      country: prev.country || "ghana",
      timezone: prev.timezone || "africa/accra",
      currency: prev.currency || "ghs",
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount

  // Update available cities when region changes
  useEffect(() => {
    if (formData.region) {
      const cities = getCitiesForRegion(formData.region);
      setAvailableCities(cities);
      
      // If current city is not in the new region's cities and not "__other__", clear it
      if (formData.city && formData.city !== "__other__" && !cities.includes(formData.city)) {
        handleInputChange("city", "");
        setCustomCity("");
      }
    } else {
      setAvailableCities([]);
      handleInputChange("city", "");
      setCustomCity("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.region]);

  const validateForm = () => {
    const newErrors = {};

    // Common validations - MANDATORY FIELDS
    if (!formData.firstName?.trim()) {
      newErrors.firstName = "First name is required (must match valid ID)";
    }

    if (!formData.lastName?.trim()) {
      newErrors.lastName = "Last name is required (must match valid ID)";
    }

    if (!formData.email?.trim()) {
      newErrors.email = "Email address is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    if (!formData.phone?.trim()) {
      newErrors.phone = "Phone number is required (WhatsApp-enabled)";
    } else {
      // Country-specific phone validation using normalized phone number
      const countryCode = formData.phoneCountry || "233";
      const normalizedPhone = normalizePhoneNumber(formData.phone, countryCode);
      
      let isValid = false;
      let errorMessage = "Please enter a valid phone number";
      
      switch (countryCode) {
        case "233": // Ghana
          isValid = /^[2-9]\d{8}$/.test(normalizedPhone);
          errorMessage = "Ghana phone numbers should be 9 digits starting with 2-9 (e.g., 0249158582 or 249158582)";
          break;
        case "234": // Nigeria
          isValid = /^[789]\d{9}$/.test(normalizedPhone);
          errorMessage = "Nigerian phone numbers should be 10 digits starting with 7, 8, or 9 (e.g., 08012345678 or 8012345678)";
          break;
        case "254": // Kenya
          isValid = /^[17]\d{8}$/.test(normalizedPhone);
          errorMessage = "Kenyan phone numbers should be 9 digits starting with 1 or 7 (e.g., 0712345678 or 712345678)";
          break;
        case "27": // South Africa
          isValid = /^[6-8]\d{8}$/.test(normalizedPhone);
          errorMessage = "South African phone numbers should be 9 digits starting with 6, 7, or 8 (e.g., 0821234567 or 821234567)";
          break;
        case "1": // US/Canada
          isValid = /^\d{10}$/.test(normalizedPhone);
          errorMessage = "US/Canada phone numbers should be 10 digits";
          break;
        case "44": // UK
          isValid = /^[1-9]\d{9}$/.test(normalizedPhone);
          errorMessage = "UK phone numbers should be 10 digits (e.g., 07700123456 or 7700123456)";
          break;
        case "61": // Australia
          isValid = /^[2-9]\d{8}$/.test(normalizedPhone);
          errorMessage = "Australian phone numbers should be 9 digits starting with 2-9 (e.g., 0412345678 or 412345678)";
          break;
        default:
          isValid = /^\d{7,15}$/.test(normalizedPhone);
          errorMessage = "Please enter a valid phone number (7-15 digits)";
      }
      
      if (!isValid) {
        newErrors.phone = errorMessage;
      }
    }

    // MANDATORY: Country
    if (!formData.country?.trim()) {
      newErrors.country = "Country is required";
    }

    // MANDATORY: City/Town
    if (!formData.city?.trim() || formData.city === "__other__") {
      newErrors.city = "City/Town is required. Please select or enter a custom location.";
    }

    // MANDATORY: ID Type (required before first payout)
    if (!formData.idType?.trim()) {
      newErrors.idType = "ID Type is required (must be completed before first payout)";
    }

    // MANDATORY: ID Number (if ID Type is selected)
    if (formData.idType?.trim() && !formData.idNumber?.trim()) {
      newErrors.idNumber = "ID Number is required";
    }

    // MANDATORY: Other ID Name (if "Other Approved ID" is selected)
    if (formData.idType === "other" && !formData.otherIdName?.trim()) {
      newErrors.otherIdName = "Please specify the ID type you are using";
    }

    // Account type specific validations
    if (accountType === "company") {
      if (!formData.companyWebsite?.trim()) {
        newErrors.companyWebsite = "Company website is required";
      }
      if (!formData.linkedinPage?.trim()) {
        newErrors.linkedinPage = "LinkedIn company page is required";
      }
      if (!formData.timezone?.trim()) {
        newErrors.timezone = "Timezone is required";
      }
      if (!formData.currency?.trim()) {
        newErrors.currency = "Currency is required";
      }
    } else if (accountType === "individual") {
      if (!formData.timezone?.trim()) {
        newErrors.timezone = "Timezone is required";
      }
      if (!formData.currency?.trim()) {
        newErrors.currency = "Currency is required";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      // Store the normalized phone number (local number without country code)
      // Country code will be appended during submission in Register.jsx
      const countryCode = "233"; // Locked to Ghana
      const normalizedPhone = normalizePhoneNumber(formData.phone, countryCode);
      
      // Debug logging
      console.log("Step1 submission debug:", {
        originalPhone: formData.phone,
        normalizedPhone: normalizedPhone,
        countryCode: countryCode
      });
      
      // Ensure phone country is set to 233 and Ghana values are set
      setFormData((prev) => ({ 
        ...prev, 
        phone: normalizedPhone,
        phoneCountry: "233",
        country: prev.country || "ghana",
        timezone: prev.timezone || "africa/accra",
        currency: prev.currency || "ghs"
      }));
      onNext();
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handlePhoneChange = (value) => {
    if (!value) {
      handleInputChange("phone", "");
      return;
    }
    
    // Allow digits only
    let cleanPhone = value.replace(/[^\d]/g, "");
    
    // Handle numbers starting with 0 or 233
    if (cleanPhone.startsWith("233")) {
      // If starts with 233, remove it to get local number
      cleanPhone = cleanPhone.substring(3);
    } else if (cleanPhone.startsWith("0")) {
      // If starts with 0, remove the leading 0
      cleanPhone = cleanPhone.substring(1);
    }
    
    // Limit to 9 digits (Ghana mobile numbers without country code)
    cleanPhone = cleanPhone.slice(0, 9);
    
    handleInputChange("phone", cleanPhone);
  };

  // Smart phone number normalization
  const normalizePhoneNumber = (phoneNumber, countryCode) => {
    if (!phoneNumber) return "";
    
    const cleanPhone = phoneNumber.replace(/[^\d]/g, "");
    
    // Country-specific normalization
    switch (countryCode) {
      case "233": // Ghana
        // Remove leading 0 from Ghana mobile numbers
        // 0249158582 -> 249158582
        if (cleanPhone.startsWith("0") && cleanPhone.length === 10) {
          return cleanPhone.substring(1);
        }
        break;
      case "234": // Nigeria
        // Remove leading 0 from Nigerian mobile numbers
        // 08012345678 -> 8012345678
        if (cleanPhone.startsWith("0") && cleanPhone.length === 11) {
          return cleanPhone.substring(1);
        }
        break;
      case "254": // Kenya
        // Remove leading 0 from Kenyan mobile numbers
        // 0712345678 -> 712345678
        if (cleanPhone.startsWith("0") && cleanPhone.length === 10) {
          return cleanPhone.substring(1);
        }
        break;
      case "27": // South Africa
        // Remove leading 0 from South African mobile numbers
        // 0821234567 -> 821234567
        if (cleanPhone.startsWith("0") && cleanPhone.length === 10) {
          return cleanPhone.substring(1);
        }
        break;
      case "44": // UK
        // Remove leading 0 from UK mobile numbers
        // 07700123456 -> 7700123456
        if (cleanPhone.startsWith("0") && cleanPhone.length === 11) {
          return cleanPhone.substring(1);
        }
        break;
      case "61": // Australia
        // Remove leading 0 from Australian mobile numbers
        // 0412345678 -> 412345678
        if (cleanPhone.startsWith("0") && cleanPhone.length === 10) {
          return cleanPhone.substring(1);
        }
        break;
      default:
        // For other countries, return the phone number as-is
        return cleanPhone;
    }
    
    return cleanPhone;
  };

  // Get display value for phone input (local number only)
  const getPhoneDisplayValue = () => {
    if (!formData.phone) return "";
    // Return the stored phone number as-is (should be local number without country code)
    return formData.phone.toString();
  };

  const idTypes = [
    { value: "ghana_card", label: "Ghana Card" },
    { value: "passport", label: "Passport" },
    { value: "other", label: "Other Approved ID" },
  ];

  const accountTypeLabels = {
    individual: "Individual",
    company: "Business / Organization",
  };

  const CustomSelect = ({
    value,
    onChange,
    options,
    placeholder,
    icon: Icon,
    error,
    label,
  }) => (
    <div>
      {label && (
        <label className="block mb-2 text-sm font-medium text-gray-700">
          {label}
        </label>
      )}
      <div className="relative">
        {Icon && (
          <Icon className="absolute left-3 top-1/2 flex-shrink-0 w-5 h-5 text-gray-400 -translate-y-1/2 pointer-events-none" />
        )}
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full ${
            Icon ? "pl-10" : "pl-3"
          } pr-4 py-3 border rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 ${
            error ? "border-red-500" : "border-gray-300"
          }`}
        >
          <option value="">{placeholder}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.flag && `${option.flag} `}
              {option.label}
            </option>
          ))}
        </select>
      </div>
      {error && (
        <motion.p
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-1 text-sm text-red-500"
        >
          {error}
        </motion.p>
      )}
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.5 }}
      className="py-4 mx-auto w-full max-w-6xl"
    >
      {/* Header */}
      <div className="mb-6">
        <h2 className="mb-2 text-2xl font-bold text-gray-900">
          A. PERSONAL / BUSINESS IDENTIFICATION
          <span className="ml-2 text-red-600">(MANDATORY)</span>
        </h2>
        <p className="text-gray-600">
          {accountType === "company"
            ? "Tell us about your company and business details"
            : "Tell us about yourself and your location"}
        </p>
      </div>

      {/* UX Guidelines Section */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-4 mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200"
      >
        <div className="flex items-start space-x-3">
          <Info className="flex-shrink-0 w-5 h-5 mt-0.5 text-blue-600" />
          <div className="flex-1">
            <h3 className="mb-2 text-sm font-semibold text-blue-900">
              Why this information matters:
            </h3>
            <div className="grid grid-cols-1 gap-2 text-xs text-blue-800 md:grid-cols-3">
              <div className="flex items-start space-x-2">
                <Shield className="flex-shrink-0 w-4 h-4 mt-0.5" />
                <span>Fraud prevention</span>
              </div>
              <div className="flex items-start space-x-2">
                <CreditCard className="flex-shrink-0 w-4 h-4 mt-0.5" />
                <span>Payment validation</span>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle2 className="flex-shrink-0 w-4 h-4 mt-0.5" />
                <span>Compliance with platform policies</span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Account Type Display */}
      <div className="p-4 mb-6 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <UserCheck className="w-5 h-5 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">Account Type:</span>
            <span className="px-3 py-1 text-sm font-semibold text-gray-900 bg-white rounded-md border border-gray-300">
              {accountTypeLabels[accountType] || accountType}
            </span>
          </div>
          <span className="text-xs text-gray-500">Selected in previous step</span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Account Display Name */}
        <div>
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Account Display Name
          </label>
          <div className="relative">
            <UserCheck className="absolute left-3 top-1/2 flex-shrink-0 w-5 h-5 text-gray-400 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={formData.accountDisplayName || ""}
              onChange={(e) =>
                handleInputChange("accountDisplayName", e.target.value)
              }
              className={`w-full pl-10 pr-4 py-3 border rounded-lg bg-white text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 ${
                errors.accountDisplayName ? "border-red-500" : "border-gray-300"
              }`}
              placeholder={
                accountType === "company"
                  ? "Enter company name"
                  : "Enter your full name"
              }
            />
          </div>
          {errors.accountDisplayName && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-1 text-sm text-red-500"
            >
              {errors.accountDisplayName}
            </motion.p>
          )}
        </div>

        {/* Personal Information - Full Name */}
        <div className="p-4 mb-4 bg-amber-50 rounded-lg border border-amber-200">
          <div className="flex items-start mb-3 space-x-2">
            <AlertCircle className="flex-shrink-0 w-5 h-5 mt-0.5 text-amber-600" />
            <div>
              <p className="text-sm font-medium text-amber-900">
                Full Name (as per valid ID)
              </p>
              <p className="mt-1 text-xs text-amber-700">
                Please ensure your name matches exactly as it appears on your valid identification document. This is required for verification and payment processing.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="flex items-center mb-2 space-x-1 text-sm font-medium text-gray-700">
              <span>First Name</span>
              <span className="text-red-600">*</span>
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 flex-shrink-0 w-5 h-5 text-gray-400 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={formData.firstName || ""}
                onChange={(e) => handleInputChange("firstName", e.target.value)}
                className={`w-full pl-10 pr-4 py-3 border rounded-lg bg-white text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 ${
                  errors.firstName ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Enter your first name (as per ID)"
              />
            </div>
            {errors.firstName && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-1 text-sm text-red-500"
              >
                {errors.firstName}
              </motion.p>
            )}
          </div>

          <div>
            <label className="flex items-center mb-2 space-x-1 text-sm font-medium text-gray-700">
              <span>Last Name</span>
              <span className="text-red-600">*</span>
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 flex-shrink-0 w-5 h-5 text-gray-400 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={formData.lastName || ""}
                onChange={(e) => handleInputChange("lastName", e.target.value)}
                className={`w-full pl-10 pr-4 py-3 border rounded-lg bg-white text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 ${
                  errors.lastName ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Enter your last name (as per ID)"
              />
            </div>
            {errors.lastName && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-1 text-sm text-red-500"
              >
                {errors.lastName}
              </motion.p>
            )}
          </div>
        </div>

        {/* Company Website (Company only) */}
        {accountType === "company" && (
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">
              Company Website
            </label>
            <div className="relative">
              <Globe className="absolute left-3 top-1/2 flex-shrink-0 w-5 h-5 text-gray-400 -translate-y-1/2 pointer-events-none" />
              <input
                type="url"
                value={formData.companyWebsite || ""}
                onChange={(e) =>
                  handleInputChange("companyWebsite", e.target.value)
                }
                className={`w-full pl-10 pr-4 py-3 border rounded-lg bg-white text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 ${
                  errors.companyWebsite ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="https://yourcompany.com"
              />
            </div>
            {errors.companyWebsite && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-1 text-sm text-red-500"
              >
                {errors.companyWebsite}
              </motion.p>
            )}
          </div>
        )}

        {/* LinkedIn Company Page (Company only) */}
        {accountType === "company" && (
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">
              LinkedIn Company Page
            </label>
            <div className="relative">
              <Linkedin className="absolute left-3 top-1/2 flex-shrink-0 w-5 h-5 text-gray-400 -translate-y-1/2 pointer-events-none" />
              <input
                type="url"
                value={formData.linkedinPage || ""}
                onChange={(e) =>
                  handleInputChange("linkedinPage", e.target.value)
                }
                className={`py-3 pr-4 pl-10 w-full placeholder-gray-500 text-gray-900 bg-white rounded-lg border transition-all duration-200 focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                  errors.linkedinPage ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="https://linkedin.com/company/yourcompany"
              />
            </div>
            {errors.linkedinPage && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-1 text-sm text-red-500"
              >
                {errors.linkedinPage}
              </motion.p>
            )}
          </div>
        )}

        {/* Email and Phone */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="flex items-center mb-2 space-x-1 text-sm font-medium text-gray-700">
              <span>Email Address</span>
              <span className="text-red-600">*</span>
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 flex-shrink-0 w-5 h-5 text-gray-400 -translate-y-1/2 pointer-events-none" />
              <input
                type="email"
                value={formData.email || ""}
                onChange={(e) => handleInputChange("email", e.target.value)}
                className={`w-full pl-10 pr-4 py-3 border rounded-lg bg-white text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 ${
                  errors.email ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Enter your email address"
              />
            </div>
            {errors.email && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-1 text-sm text-red-500"
              >
                {errors.email}
              </motion.p>
            )}
          </div>

          <div>
            <label className="flex items-center mb-2 space-x-1 text-sm font-medium text-gray-700">
              <span>Phone Number</span>
              <span className="text-red-600">*</span>
              <MessageCircle className="w-4 h-4 text-green-600" title="WhatsApp-enabled" />
            </label>
            <div className="flex space-x-2">
              {/* Country Code Display (Locked to Ghana) */}
              <div className="w-48">
                <div className="px-3 py-3 w-full text-gray-700 bg-gray-100 rounded-lg border border-gray-300 cursor-not-allowed">
                  🇬🇭 Ghana (+233)
                </div>
                <input type="hidden" value="233" />
              </div>
              
              {/* Phone Number Input */}
              <div className="flex-1">
                <input
                  type="tel"
                  value={getPhoneDisplayValue()}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  placeholder="0249158582 or 233249158582"
                  maxLength="12"
                  pattern="[0-9]*"
                  inputMode="numeric"
                  className={`w-full px-3 py-3 border rounded-lg bg-white text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 ${
                    errors.phone ? "border-red-500" : "border-gray-300"
                  }`}
                />
              </div>
            </div>
            <div className="flex items-center mt-1 space-x-2">
              <MessageCircle className="w-3 h-3 text-green-600" />
              <p className="text-xs text-gray-500">
                WhatsApp-enabled number required. Enter with 0 (e.g., 0249158582) or 233 (e.g., 233249158582)
              </p>
            </div>
            {errors.phone && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-1 text-sm text-red-500"
              >
                {errors.phone}
              </motion.p>
            )}
          </div>
        </div>

        {/* Mailing Address */}
        <div>
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Mailing Address
          </label>
          <div className="space-y-3">
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 flex-shrink-0 w-5 h-5 text-gray-400 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={formData.streetAddress1 || ""}
                onChange={(e) =>
                  handleInputChange("streetAddress1", e.target.value)
                }
                className="py-3 pr-4 pl-10 w-full placeholder-gray-500 text-gray-900 bg-white rounded-lg border border-gray-300 transition-all duration-200 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Street address 1"
              />
            </div>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 flex-shrink-0 w-5 h-5 text-gray-400 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={formData.streetAddress2 || ""}
                onChange={(e) =>
                  handleInputChange("streetAddress2", e.target.value)
                }
                className="py-3 pr-4 pl-10 w-full placeholder-gray-500 text-gray-900 bg-white rounded-lg border border-gray-300 transition-all duration-200 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Street address 2 (optional)"
              />
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              {/* Region Selection */}
              <div>
                <SearchableSelect
                  value={formData.region || ""}
                  onChange={(value) => handleInputChange("region", value)}
                  options={getAllRegions()}
                  placeholder="Select Region"
                  label={
                    <span className="flex items-center space-x-1">
                      <span>Region</span>
                    </span>
                  }
                  icon={Building2}
                  error={errors.region}
                  showOtherOption={false}
                />
              </div>

              {/* City / Town Selection */}
              <div>
                <SearchableSelect
                  value={formData.city === "__other__" ? "__other__" : formData.city || ""}
                  onChange={(value) => {
                    if (value === "__other__") {
                      handleInputChange("city", "__other__");
                      setCustomCity("");
                    } else {
                      handleInputChange("city", value);
                      setCustomCity("");
                    }
                  }}
                  options={availableCities}
                  placeholder={formData.region ? "Select City / Town" : "Select region first"}
                  disabled={!formData.region}
                  label={
                    <span className="flex items-center space-x-1">
                      <span>City / Town</span>
                      <span className="text-red-600">*</span>
                    </span>
                  }
                  icon={MapPin}
                  error={errors.city}
                  showOtherOption={true}
                  otherOptionLabel="Other (Custom Location)"
                  emptyMessage="No cities found. Select 'Other' to enter custom location."
                />

                {/* Custom City Input */}
                <AnimatePresence>
                  {formData.city === "__other__" && (
                    <motion.div
                      initial={{ opacity: 0, height: 0, marginTop: 0 }}
                      animate={{ opacity: 1, height: "auto", marginTop: 8 }}
                      exit={{ opacity: 0, height: 0, marginTop: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 flex-shrink-0 w-5 h-5 text-orange-500 -translate-y-1/2 pointer-events-none" />
                        <input
                          type="text"
                          value={customCity}
                          onChange={(e) => {
                            const value = e.target.value;
                            setCustomCity(value);
                            // Update the actual city field with custom value
                            handleInputChange("city", value);
                          }}
                          placeholder="Enter your custom location"
                          className={`py-3 pr-4 pl-10 w-full text-gray-900 rounded-lg border-2 transition-all duration-200 focus:ring-2 focus:bg-white ${
                            errors.city && (!customCity || customCity === "__other__")
                              ? "placeholder-red-400 bg-red-50 border-red-300 focus:ring-red-500 focus:border-transparent"
                              : "placeholder-orange-400 bg-orange-50 border-orange-300 focus:ring-orange-500 focus:border-transparent"
                          }`}
                          autoFocus
                        />
                      </div>
                      <p className={`mt-1 text-xs flex items-center space-x-1 ${
                        errors.city && (!customCity || customCity === "__other__")
                          ? "text-red-600"
                          : "text-orange-600"
                      }`}>
                        <span>{errors.city && (!customCity || customCity === "__other__") ? "⚠️" : "✏️"}</span>
                        <span>{errors.city && (!customCity || customCity === "__other__") ? errors.city : "Enter your custom city or town name"}</span>
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Zip Code */}
              <div>
                <label className="flex items-center mb-1 space-x-1 text-xs font-medium text-gray-700">
                  <span>Zip Code</span>
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 flex-shrink-0 w-5 h-5 text-gray-400 -translate-y-1/2 pointer-events-none" />
                  <input
                    type="text"
                    value={formData.zipCode || ""}
                    onChange={(e) => handleInputChange("zipCode", e.target.value)}
                    className="py-3 pr-4 pl-10 w-full placeholder-gray-500 text-gray-900 bg-white rounded-lg border border-gray-300 transition-all duration-200 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Zip Code"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ID Type - Required before first payout */}
        <div className="p-4 mb-4 bg-purple-50 rounded-lg border border-purple-200">
          <div className="flex items-start space-x-2">
            <FileText className="flex-shrink-0 w-5 h-5 mt-0.5 text-purple-600" />
            <div className="flex-1">
              <p className="mb-3 text-xs text-purple-700">
                <strong>Note:</strong> ID Type must be completed before your first payout. Please select the type of identification document you will use for verification.
              </p>
              <div className="space-y-4">
                <CustomSelect
                  value={formData.idType || ""}
                  onChange={(value) => handleInputChange("idType", value)}
                  options={idTypes}
                  placeholder="Select ID Type"
                  icon={FileText}
                  error={errors.idType}
                  label={
                    <span>
                      ID Type <span className="text-red-600">*</span>
                    </span>
                  }
                />

                {/* Show "Other ID Name" field if "Other Approved ID" is selected */}
                {formData.idType === "other" && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <label className="flex items-center mb-2 space-x-1 text-sm font-medium text-gray-700">
                      <span>Specify ID Type</span>
                      <span className="text-red-600">*</span>
                    </label>
                    <div className="relative">
                      <FileText className="absolute left-3 top-1/2 flex-shrink-0 w-5 h-5 text-gray-400 -translate-y-1/2 pointer-events-none" />
                      <input
                        type="text"
                        value={formData.otherIdName || ""}
                        onChange={(e) => handleInputChange("otherIdName", e.target.value)}
                        className={`py-3 pr-4 pl-10 w-full placeholder-gray-500 text-gray-900 bg-white rounded-lg border transition-all duration-200 focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                          errors.otherIdName ? "border-red-500" : "border-gray-300"
                        }`}
                        placeholder="e.g., National ID, Driver's License, Voter ID"
                      />
                    </div>
                    {errors.otherIdName && (
                      <motion.p
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-1 text-sm text-red-500"
                      >
                        {errors.otherIdName}
                      </motion.p>
                    )}
                  </motion.div>
                )}

                {/* Show ID Number field if ID Type is selected */}
                {formData.idType && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <label className="flex items-center mb-2 space-x-1 text-sm font-medium text-gray-700">
                      <span>
                        {formData.idType === "ghana_card"
                          ? "Ghana Card Number"
                          : formData.idType === "passport"
                          ? "Passport Number"
                          : formData.otherIdName
                          ? `${formData.otherIdName} Number`
                          : "ID Number"}
                      </span>
                      <span className="text-red-600">*</span>
                    </label>
                    <div className="relative">
                      <CreditCard className="absolute left-3 top-1/2 flex-shrink-0 w-5 h-5 text-gray-400 -translate-y-1/2 pointer-events-none" />
                      <input
                        type="text"
                        value={formData.idNumber || ""}
                        onChange={(e) => handleInputChange("idNumber", e.target.value)}
                        className={`py-3 pr-4 pl-10 w-full placeholder-gray-500 text-gray-900 bg-white rounded-lg border transition-all duration-200 focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                          errors.idNumber ? "border-red-500" : "border-gray-300"
                        }`}
                        placeholder={
                          formData.idType === "ghana_card"
                            ? "e.g., GHA-123456789-0"
                            : formData.idType === "passport"
                            ? "e.g., G1234567"
                            : "Enter your ID number"
                        }
                      />
                    </div>
                    {errors.idNumber && (
                      <motion.p
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-1 text-sm text-red-500"
                      >
                        {errors.idNumber}
                      </motion.p>
                    )}
                  </motion.div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-between">
          <motion.button
            type="button"
            onClick={onPrev}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex items-center px-6 py-3 space-x-2 text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </motion.button>

          <motion.button
            type="submit"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="px-6 py-3 font-medium text-white bg-gradient-to-r from-red-500 to-red-600 rounded-lg shadow-lg transition-all duration-200 shadow-red-500/25 hover:from-red-600 hover:to-red-700 hover:shadow-xl hover:shadow-red-500/40"
          >
            Continue
          </motion.button>
        </div>
      </form>
    </motion.div>
  );
};

export default Step1Enhanced;
